import { useState, useMemo } from 'react';
import { DatePicker, Form, Select } from 'antd';
import dayjs from 'dayjs';

import { useProducts, useBranches, useCreateReport } from '../../hooks';
import {
  DATE_FORMAT,
  formatNumber,
  getToday,
  isFutureDate,
  notify,
  removeObjectProperties,
  searchFn,
} from '../../utils';
import { Card, EmptyState, FormActions, FormSkeleton, PageHeader, QtyRow, SearchInput } from '../../components';
import { ROUTE_URLS } from '../../routes';
import { useI18n } from '../../i18n';

const sumPositive = values =>
  Object.entries(values ?? {}).reduce(
    (acc, [key, value]) => {
      if (key === 'branchId' || key === 'date') return acc;
      const n = Number(value) || 0;
      return n > 0 ? { filled: acc.filled + 1, units: acc.units + n } : acc;
    },
    { filled: 0, units: 0 },
  );

export const CreateReport = () => {
  const { t } = useI18n();
  const [searchText, setSearchText] = useState('');
  const [summary, setSummary] = useState({ filled: 0, units: 0 });

  const { data: products, isLoading: isProductsLoading } = useProducts();
  const { data: branches, isLoading: isBranchesLoading } = useBranches();
  const { mutateAsync: createReport, isLoading } = useCreateReport();

  const defaultValues = useMemo(() => {
    const values = {
      date: dayjs(getToday()),
    };
    if (Array.isArray(branches) && branches.length === 1) {
      values.branchId = branches[0].id;
    }
    products?.forEach(product => {
      values[product.id] = 0;
    });
    return values;
  }, [products, branches]);

  const visibleIds = useMemo(() => {
    if (!searchText) return null;
    return new Set(searchFn(products, searchText).map(product => product.id));
  }, [products, searchText]);

  const isValidReport = sales => {
    for (const key in sales) {
      if (+sales[key] < 0) {
        return false;
      }
    }

    return true;
  };

  const onFinish = values => {
    const {
      branchId: branch_id,
      date,
      ...sales
    } = removeObjectProperties(values, ['productName']);
    if (!isValidReport(sales)) {
      return notify('error', t('createReport.negativeError'));
    }

    const readyValues = {
      branch_id,
      report_date: dayjs(date).format(DATE_FORMAT),
      product_ids: sales,
    };
    createReport(readyValues);
  };

  const required = field => [{ required: true, message: t('validation.required', { field }) }];

  return (
    <div className='wm-form-page'>
      <PageHeader title={t('createReport.title')} subtitle={t('createReport.subtitle')} />
      {isProductsLoading || isBranchesLoading ? (
        <Card>
          <FormSkeleton fields={6} />
        </Card>
      ) : (
        <Form
          name='report'
          layout='vertical'
          onFinish={onFinish}
          onFinishFailed={() => setSearchText('')}
          onValuesChange={(_, all) => setSummary(sumPositive(all))}
          initialValues={defaultValues}
          requiredMark={false}
        >
          <Card className='wm-form-section'>
            <div className='wm-field-grid'>
              <Form.Item label={t('createReport.branch')} name='branchId' rules={required(t('createReport.branch'))}>
                <Select
                  showSearch
                  optionFilterProp='label'
                  placeholder={t('createReport.branchPlaceholder')}
                  options={(branches ?? []).map(branch => ({ value: branch.id, label: branch.name }))}
                />
              </Form.Item>
              <Form.Item label={t('createReport.date')} name='date' rules={required(t('createReport.date'))}>
                <DatePicker
                  format='DD.MM.YYYY'
                  allowClear={false}
                  disabledDate={isFutureDate}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </div>
          </Card>

          <Card
            className='wm-form-section'
            title={t('createReport.products')}
            subtitle={t('createReport.soldQuantity')}
            extra={
              <SearchInput
                value={searchText}
                onChange={setSearchText}
                placeholder={t('createReport.searchPlaceholder')}
              />
            }
          >
            {!products?.length && <EmptyState compact />}
            {visibleIds && visibleIds.size === 0 && <EmptyState variant='search' compact />}
            <div className='wm-qty-list'>
              {products?.map(product => (
                <QtyRow
                  key={product.id}
                  name={product.id}
                  title={product.name}
                  hidden={visibleIds && !visibleIds.has(product.id)}
                  meta={
                    product.quantity !== undefined
                      ? t('createReport.inStock', { count: formatNumber(product.quantity) })
                      : null
                  }
                  placeholder={t('createReport.soldQuantity')}
                  required={false}
                />
              ))}
            </div>
          </Card>
          <FormActions
            cancelTo={ROUTE_URLS.HOME}
            submitting={isLoading}
            submitLabel={t('nav.createReport')}
            info={
              summary.filled > 0 && (
                <>
                  <b>{t('createReport.filled', { count: summary.filled })}</b>
                  <span>{t('createReport.totalUnits', { count: formatNumber(summary.units) })}</span>
                </>
              )
            }
          />
        </Form>
      )}
    </div>
  );
};
